<template>
  <div>
    <div class="d-flex align-center">
      <AppButton icon
                 size="small"
                 @click="expandMembers = !expandMembers"
      >
        <app-material-icon :name="expandMembers ? 'keyboard_arrow_down' : 'keyboard_arrow_right'"
                          color="icon-secondary"
        />
      </AppButton>
      <v-checkbox v-model="isGroupSelected"
                  :label="group.name"
                  hide-details
                  :disabled="isGroupDisabled"
                  :indeterminate="groupIsPartiallySelected"
      />
    </div>
    <DocumentSharePickerMember v-for="member in group.members"
                                v-show="expandMembers"
                                :key="`member-${member.id}`"
                                :group-is-forced="isGroupForced || forceCurrentUserSelection && member.id === currentUserId"
                                :group-is-selected="isGroupSelected || forceCurrentUserSelection && member.id === currentUserId"
                                :multi-select="multiSelect"
                                style="padding-left: 80px"
                                :member="member"
                                :disabled="forceCurrentUserSelection && member.id === currentUserId"
                                :users-only="usersOnly"
                                @removeMemberFromSelectedGroup="removeMemberFromSelectedGroup"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'

import AppButton from '@/common/buttons/AppButton.vue'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { DocumentSharePickerMutationTypes } from '@/store/modules/document-share-picker/mutation_types'

import DocumentSharePickerMember from './DocumentSharePickerMember.vue'

export default {
  name: 'DocumentSharePickerGroup',
  components: { AppButton, DocumentSharePickerMember },
  props: {
    group: {
      type: Object,
      required: true,
    },
    forcePmSelection: {
      type: Boolean,
      required: true,
    },
    forceCurrentUserSelection: {
      type: Boolean,
      required: true,
    },
    usersOnly: {
      type: Boolean,
      default: false,
    },
    multiSelect: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      expandMembers: false,
    }
  },
  computed: {
    ...mapState('documentSharePicker', [
      'selectedGroups',
      'selectedMembers',
      'disabledGroups',
      'disabledMembers',
      'highlightedMember',
    ]),
    ...mapGetters('user', ['currentUserId']),
    ...mapGetters('groups', ['currentUserGroupId']),
    isGroupSelected: {
      get () {
        if (this.usersOnly) {
          return this.group.members.length > 0
            ? this.group.members.every(member => {
              return this.selectedMembers.some(selectedMember => selectedMember.id === member.id)
            })
            : false
        } else if (this.isGroupForced) {
          return true
        } else {
          return this.selectedGroups.some(group => group.id === this.group.id)
        }
      },
      set (groupIsSelected) {
        if (groupIsSelected) {
          if(this.usersOnly) {
            this.ADD_MEMBERS_TO_SELECTED(this.group.members)
          } else {
            this.SELECT_GROUP(this.group)
          }
        } else {
          if (!this.usersOnly) this.UNSELECT_GROUP(this.group)
          this.REMOVE_MEMBERS_FROM_SELECTED(this.group.members)
        }
      },
    },
    isGroupDisabled() {
      return this.isGroupForced ||
        this.disabledGroups.some(o => o.id === this.group.id) ||
        !this.multiSelect
    },
    isGroupForced () {
      return this.forcePmSelection && this.group.isPm
    },
    atLeastOneMemberIsSelected () {
      if (this.group.members.length > 0) {
        return this.group.members.some(member => {
          return this.selectedMembers.find(selectedMember => selectedMember.id === member.id)
        })
      }
      return false
    },
    groupIsPartiallySelected () {
      return this.atLeastOneMemberIsSelected && !this.isGroupSelected
    },
  },
  watch: {
    highlightedMember (value) {
      if (!value) {
        return
      }

      const highlightedMemberIsInGroup = this.group.members.some(
        member => member.id === value.id,
      )

      if (highlightedMemberIsInGroup) {
        this.expandMembers = true
      }
    },
  },
  methods: {
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.SELECT_GROUP,
      DocumentSharePickerActionTypes.UNSELECT_GROUP,
    ]),
    ...mapMutations('documentSharePicker', [
      DocumentSharePickerMutationTypes.ADD_MEMBERS_TO_SELECTED,
      DocumentSharePickerMutationTypes.REMOVE_MEMBERS_FROM_SELECTED,
      DocumentSharePickerMutationTypes.SET_SELECTED_MEMBERS,
    ]),
    removeMemberFromSelectedGroup (memberIdToRemove) {
      /*
      * When we unselect a member from a selected group we have to remove the group from the selectedGroups state in case the group toggle was selected beforehand.
      * A selected group doesn't have its members in the selectedMembers state since it directly maps into selectedGroups.
      * So after removing the group, we need to add all its members except the unselected one in the selectedMembers state to workaround groups and members being separate.
      */
      if (!this.usersOnly) this.UNSELECT_GROUP(this.group)
      this.REMOVE_MEMBERS_FROM_SELECTED(this.group.members)

      this.group.members.forEach(member => {
        if (member.id !== memberIdToRemove) {
          if (this.multiselect) {
            this.ADD_MEMBERS_TO_SELECTED([member])
          } else {
            this.SET_SELECTED_MEMBERS([member])
          }
        }
      })
    },
  },
}
</script>

